/**
 * LEClosingCosts - Closing costs sections A-J
 *
 * Displays the full closing costs breakdown per TRID format
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Receipt } from 'lucide-react';
import { LESection, LESectionDivider } from './LESection';
import { LEFeeRow, LEFeeGroup } from './LEFeeRow';
import type { ParsedLoanEstimate, FeeSection, FeeItem } from './types';

interface LEClosingCostsProps {
  data: ParsedLoanEstimate;
  className?: string;
}

/**
 * Format currency for display (always show cents)
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Group Section C fees by payee for better organization
 */
function groupFeesByPayee(fees: FeeItem[]): { payee: string; fees: FeeItem[]; total: number }[] {
  const groups = new Map<string, FeeItem[]>();

  for (const fee of fees) {
    const payee = fee.paidTo || 'Unknown Payee';
    const existing = groups.get(payee) || [];
    existing.push(fee);
    groups.set(payee, existing);
  }

  // Convert to array and calculate totals
  const result = Array.from(groups.entries()).map(([payee, feeList]) => ({
    payee,
    fees: feeList.sort((a, b) => b.amount - a.amount),
    total: feeList.reduce((sum, f) => sum + f.amount, 0)
  }));

  // Sort groups: known payees first (alphabetically), then "Unknown Payee" last
  return result.sort((a, b) => {
    if (a.payee === 'Unknown Payee') {
return 1;
}
    if (b.payee === 'Unknown Payee') {
return -1;
}
    return a.payee.localeCompare(b.payee);
  });
}

export const LEClosingCosts: React.FC<LEClosingCostsProps> = ({
  data,
  className
}) => {
  const cc = data.closingCosts;

  return (
    <LESection
      title="Closing Cost Details"
      icon={<Receipt className="h-4 w-4" />}
      className={className}
      rightContent={
        <span className="text-sm font-semibold tabular-nums">
          {formatCurrency(cc.totalClosingCosts)}
        </span>
      }
    >
      <div className="space-y-2">
        {/* ============================================ */}
        {/* LOAN COSTS (Sections A-D) */}
        {/* ============================================ */}
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground pb-1">
          Loan Costs
        </div>

        {/* Section A - Origination Charges */}
        <FeeSectionDisplay section={cc.sectionA} showMissingAlert showValidation />

        {/* Section B - Services You Cannot Shop For */}
        <FeeSectionDisplay section={cc.sectionB} showMissingAlert />

        {/* A+B Subtotal with validation */}
        <ABSubtotalRow
          sectionATotal={cc.sectionA.totalWithoutDiscount ?? cc.sectionA.total}
          sectionBTotal={cc.sectionB.total}
          encompassTotalAB={cc.encompassTotalAB}
        />

        {/* Section C - Services You Can Shop For (grouped by payee) */}
        <SectionCGroupedDisplay section={cc.sectionC} showValidation />

        {/* D. Total Loan Costs */}
        <LEFeeRow
          description="D. TOTAL LOAN COSTS (A + B + C)"
          amount={cc.totalLoanCosts}
          isSubtotal
        />

        <LESectionDivider />

        {/* ============================================ */}
        {/* OTHER COSTS (Sections E-I) */}
        {/* ============================================ */}
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground pb-1">
          Other Costs
        </div>

        {/* Section E - Taxes and Other Government Fees */}
        <FeeSectionDisplay section={cc.sectionE} showMissingAlert showValidation />

        {/* Section F - Prepaids */}
        <FeeSectionDisplay section={cc.sectionF} showValidation />

        {/* Section G - Initial Escrow Payment at Closing */}
        <FeeSectionDisplay section={cc.sectionG} showValidation />

        {/* Section H - Other */}
        {cc.sectionH.total > 0 && <FeeSectionDisplay section={cc.sectionH} />}

        {/* I. Total Other Costs */}
        <LEFeeRow
          description="I. TOTAL OTHER COSTS (E + F + G + H)"
          amount={cc.totalOtherCosts}
          isSubtotal
        />

        <LESectionDivider />

        {/* ============================================ */}
        {/* TOTAL CLOSING COSTS (J) */}
        {/* ============================================ */}

        {/* Lender Credits */}
        {cc.lenderCredits > 0 && (
          <LEFeeRow
            description="Lender Credits"
            amount={cc.lenderCredits}
            negative
          />
        )}

        {/* J. Total Closing Costs */}
        <TotalClosingCostsRow
          amount={cc.totalClosingCosts}
          encompassTotal={cc.encompassTotalClosingCosts}
        />
      </div>
    </LESection>
  );
};

/**
 * Fee section display with expandable items
 */
interface FeeSectionDisplayProps {
  section: FeeSection;
  defaultExpanded?: boolean;
  showMissingAlert?: boolean;
  showValidation?: boolean; // Whether to show green/red validation ring
}

const FeeSectionDisplay: React.FC<FeeSectionDisplayProps> = ({
  section,
  defaultExpanded = true,
  showMissingAlert = false,
  showValidation = false
}) => {
  if (section.total === 0 && section.items.length === 0) {
    return null;
  }

  return (
    <LEFeeGroup
      label={section.label}
      total={section.total}
      items={section.items}
      defaultExpanded={defaultExpanded}
      showMissingAlert={showMissingAlert}
      encompassTotal={section.encompassTotal}
      showValidation={showValidation}
      totalWithoutDiscount={section.totalWithoutDiscount}
    />
  );
};

/**
 * Section C display with fees grouped by payee
 */
const SectionCGroupedDisplay: React.FC<{ section: FeeSection; showValidation?: boolean }> = ({ section, showValidation = false }) => {
  const [expanded, setExpanded] = React.useState(true);
  const groups = groupFeesByPayee(section.items);

  if (section.total === 0 && section.items.length === 0) {
    return null;
  }

  // Validation: compare calculated total with Encompass total (allow penny rounding)
  const isValidated = showValidation && section.encompassTotal !== undefined;
  const isMatch = isValidated ? Math.abs(section.total - section.encompassTotal!) < 0.01 : null;

  return (
    <div className="space-y-1">
      {/* Section header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between py-2 text-sm hover:bg-muted/30 px-2 -mx-2 rounded transition-colors"
      >
        <span className="font-medium">{section.label}</span>
        <div className="flex items-center gap-2">
          <span className={cn(
            "font-semibold tabular-nums",
            isValidated && "px-2 py-0.5 rounded",
            isMatch === true && "ring-2 ring-green-500 bg-green-50 dark:bg-green-900/20",
            isMatch === false && "ring-2 ring-red-500 bg-red-50 dark:bg-red-900/20"
          )}>{formatCurrency(section.total)}</span>
          <svg
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              expanded && 'rotate-180'
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="pl-2 space-y-3 border-l-2 border-muted ml-2">
          {groups.map((group) => (
            <div key={group.payee} className="space-y-0.5">
              {/* Payee sub-header */}
              <div className="flex items-center justify-between text-xs py-1.5 bg-muted/30 px-2 rounded">
                <span className="font-medium text-muted-foreground">{group.payee}</span>
                <span className="font-semibold tabular-nums text-muted-foreground">{formatCurrency(group.total)}</span>
              </div>
              {/* Fees in this group */}
              {group.fees.map((item, idx) => (
                <LEFeeRow
                  key={idx}
                  description={item.description}
                  feeName={item.feeName}
                  paidTo={item.paidTo}
                  amount={item.amount}
                  detail={item.detail}
                  paidToType={item.paidToType}
                  aprAffecting={item.aprAffecting}
                  showMissingAlert
                  indent
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * A+B Subtotal row with combined validation against NEWHUD2.X28
 */
const ABSubtotalRow: React.FC<{
  sectionATotal: number;
  sectionBTotal: number;
  encompassTotalAB?: number;
}> = ({ sectionATotal, sectionBTotal, encompassTotalAB }) => {
  const calculatedAB = sectionATotal + sectionBTotal;
  // Validation: compare calculated A+B with Encompass NEWHUD2.X28 (allow penny rounding)
  const isValidated = encompassTotalAB !== undefined && encompassTotalAB > 0;
  const isMatch = isValidated ? Math.abs(calculatedAB - encompassTotalAB!) < 0.01 : null;

  return (
    <div className="flex items-start justify-between py-1 text-xs text-muted-foreground pl-2 border-l-2 border-muted ml-2">
      <span>Subtotal (A + B)</span>
      <span className={cn(
        "text-right whitespace-nowrap tabular-nums",
        isValidated && "px-1.5 py-0.5 rounded text-foreground",
        isMatch === true && "ring-2 ring-green-500 bg-green-50 dark:bg-green-900/20",
        isMatch === false && "ring-2 ring-red-500 bg-red-50 dark:bg-red-900/20"
      )}>
        {formatCurrency(calculatedAB)}
      </span>
    </div>
  );
};

/**
 * Total Closing Costs row with validation
 */
const TotalClosingCostsRow: React.FC<{ amount: number; encompassTotal?: number }> = ({ amount, encompassTotal }) => {
  // Validation: compare calculated total with Encompass total (allow penny rounding)
  const isValidated = encompassTotal !== undefined;
  const isMatch = isValidated ? Math.abs(amount - encompassTotal) < 0.01 : null;

  return (
    <div className="flex items-start justify-between py-1.5 text-sm border-t pt-2 mt-2">
      <span className="font-semibold">J. TOTAL CLOSING COSTS (D + I - Lender Credits)</span>
      <span className={cn(
        "text-right whitespace-nowrap tabular-nums font-semibold text-base",
        isValidated && "px-2 py-0.5 rounded",
        isMatch === true && "ring-2 ring-green-500 bg-green-50 dark:bg-green-900/20",
        isMatch === false && "ring-2 ring-red-500 bg-red-50 dark:bg-red-900/20"
      )}>
        {formatCurrency(amount)}
      </span>
    </div>
  );
};

export default LEClosingCosts;
